import React, { useRef, useState } from 'react';
import '../styles/PhotoCapture.css';

interface PhotoCaptureProps {
  onPhotoCapture: (imageData: string) => void;
}

const PhotoCapture: React.FC<PhotoCaptureProps> = ({ onPhotoCapture }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startCamera = async () => {
    try {
      // First, set camera active to render the video element
      setIsCameraActive(true);
      
      // Small delay to ensure the video element is in the DOM
      setTimeout(async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { 
              facingMode: 'user',
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }, 
            audio: false 
          });
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
              if (videoRef.current) {
                videoRef.current.play().catch(e => {
                  console.error('Error playing video:', e);
                });
              }
            };
            setError(null);
          }
        } catch (err) {
          console.error('Error accessing camera:', err);
          setError('Could not access camera. Please make sure you have given permission and that your device has a camera.');
          setIsCameraActive(false);
        }
      }, 100);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();
      
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (canvasRef.current && videoRef.current) {
      const context = canvasRef.current.getContext('2d');
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
        
        const imageData = canvasRef.current.toDataURL('image/jpeg');
        onPhotoCapture(imageData);
        stopCamera();
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        onPhotoCapture(imageData);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="photo-capture">
      <div className="camera-container">
        {isCameraActive ? (
          <>
            <div className="camera-viewport">
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                muted
                style={{ width: '100%', height: 'auto', minHeight: '300px' }} 
              />
              <div className="camera-instructions">
                <p>Camera is active. Position yourself and click the button below to take a photo.</p>
              </div>
            </div>
            <div className="camera-controls">
              <button onClick={capturePhoto} className="capture-btn">
                <span className="camera-icon">📸</span>
                <span>Take Photo</span>
              </button>
              <button onClick={stopCamera} className="cancel-btn">
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="camera-options">
            <h3>Choose an option</h3>
            <button onClick={startCamera} className="option-btn camera-option">
              <span className="camera-icon">📷</span>
              <span>Use Camera</span>
            </button>
            <button onClick={triggerFileInput} className="option-btn upload-option">
              <span className="upload-icon">📁</span>
              <span>Upload Photo</span>
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileUpload} 
              accept="image/*" 
              style={{ display: 'none' }}
            />
            {error && <p className="error-message">{error}</p>}
          </div>
        )}
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default PhotoCapture;